import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { close, seedRendersModalState } from "./SeedRendersModalSlice";
import { useSeedRendersMutation } from "../../UsersApiSlice";

interface SeedRendersModalProps {
  readonly userId: number;
}

const SeedRendersModal = ({ userId }: SeedRendersModalProps) => {
  const dispatch = useDispatch();
  const { isOpened } = useSelector(seedRendersModalState);
  const [seedRenders, { isLoading, isSuccess, isError, reset }] = useSeedRendersMutation();

  const urlRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);
  const countRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  if (!isOpened) return null;

  const handleClose = () => {
    reset();
    setValidationError(null);
    dispatch(close());
  };

  const handleSubmit = async () => {
    const url = urlRef.current?.value?.trim() ?? "";
    const time = timeRef.current?.value ?? "";
    const count = parseInt(countRef.current?.value ?? "0", 10);

    if (!url) return setValidationError("URL is required");
    if (!time) return setValidationError("Date/time is required");
    if (!count || count < 1 || count > 5000)
      return setValidationError("Count must be between 1 and 5000");

    setValidationError(null);
    await seedRenders({ userId, url, time: new Date(time).toISOString(), count });
  };

  return (
    <>
      <div className="modal show d-block" tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Seed renders</h5>
              <button type="button" className="btn-close" onClick={handleClose} />
            </div>
            <div className="modal-body">
              <div className="mb-2">
                <small className="text-muted">
                  Inserts synthetic render records into ClickHouse and increments the Redis render
                  counter. Staging only. It may take a minute before the renders appear in the UI.
                </small>
              </div>
              <div className="form-group mb-2">
                <label className="form-label" htmlFor="seed-url">Website URL</label>
                <input id="seed-url" className="form-control" type="url" ref={urlRef}
                  placeholder="https://example.com/page" />
              </div>
              <div className="form-group mb-2">
                <label className="form-label" htmlFor="seed-time">Date / time (local)</label>
                <input id="seed-time" className="form-control" type="datetime-local" ref={timeRef} />
                <small className="text-muted">
                  Records older than your billing cycle start won't move the counter.
                </small>
              </div>
              <div className="form-group mb-2">
                <label className="form-label" htmlFor="seed-count">Number of renders (1–5000)</label>
                <input id="seed-count" className="form-control" type="number"
                  min={1} max={5000} defaultValue={100} ref={countRef} />
              </div>
              {validationError && (
                <div className="alert alert-danger py-1">{validationError}</div>
              )}
              {isError && (
                <div className="alert alert-danger py-1">
                  Seeding failed. Check that PRERENDER_ENVIRONMENT=staging is set.
                </div>
              )}
              {isSuccess && (
                <div className="alert alert-success py-1">Renders seeded successfully.</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleClose}>Close</button>
              <button className="btn btn-warning" onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? "Seeding…" : "Seed renders"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" onClick={handleClose} />
    </>
  );
};

export default SeedRendersModal;
